<template>
  <v-dialog
    :value="value"
    max-width="640"
    :persistent="sending"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title id="support-dialog-title">
        {{ $t("\n        Contact support\n      ") }}
      </v-card-title>
      <v-card-text class="pt-3">
        <v-alert
          v-if="receipt"
          type="success"
          text
          role="status"
        >
          {{ $t("\n          Your message has been sent to our support team.") }}<br />{{ $t("Reference: ") }}{{ receipt }}
        </v-alert>
        <v-form
          v-else
          ref="form"
          @submit.prevent="submit"
        >
          <p>{{ $t("Tell us what you need help with. We'll reply to your email.") }}</p>
          <v-alert
            v-if="error"
            type="error"
            text
            role="alert"
          >
            {{ error }}
          </v-alert>
          <v-select
            v-model="form.topic"
            :items="topics.map(topic => ({ text: $t(topic), value: topic }))"
            :label="$t(&quot;Topic&quot;)"
            outlined
            :disabled="sending"
          />
          <v-text-field
            v-model="form.name"
            :label="$t(&quot;Your name&quot;)"
            autocomplete="name"
            outlined
            maxlength="100"
            :rules="[required]"
            :disabled="sending"
          />
          <v-text-field
            v-model="form.email"
            :label="$t(&quot;Email associated with your account&quot;)"
            type="email"
            autocomplete="email"
            outlined
            maxlength="254"
            :rules="[emailRule]"
            :disabled="sending"
          />
          <v-text-field
            v-model="form.device"
            :label="$t(&quot;Device and browser (optional)&quot;)"
            outlined
            maxlength="300"
            :disabled="sending"
          />
          <v-textarea
            v-model="form.message"
            :label="$t(&quot;How can we help?&quot;)"
            outlined
            rows="5"
            counter="5000"
            maxlength="5000"
            :rules="[messageRule]"
            :disabled="sending"
          />
          <input
            v-model="form.website"
            class="support-trap"
            aria-hidden="true"
            tabindex="-1"
            autocomplete="off"
            name="website"
          />
          <p class="text-caption">
            {{ $t("\n            Please never send us your password.\n          ") }}
          </p>
          <div class="support-dialog-actions">
            <v-btn
              text
              :disabled="sending"
              @click="$emit('input', false)"
            >
              {{ $t("\n              Cancel\n            ") }}
            </v-btn>
            <v-btn
              color="primary"
              type="submit"
              :loading="sending"
            >
              {{ $t("\n              Send message\n            ") }}
            </v-btn>
          </div>
        </v-form>
        <v-btn
          v-if="receipt"
          color="primary"
          @click="$emit('input', false)"
        >
          {{ $t("\n          Done\n        ") }}
        </v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import { Auth } from 'aws-amplify'
import { t } from '@/i18n'

import support, { supportTopics, supportRequestId } from '@/services/support'

export default {
  name: 'SupportDialog',
  props: { value: Boolean, topic: { type: String, default: 'Something Else' } },
  data: () => ({
    topics: supportTopics,
    form: {
      name: '', email: '', topic: 'Something Else', device: '', message: '', website: '',
    },
    sending: false,
    error: '',
    receipt: '',
    requestId: '',
    lastPayload: '',
    required: value => !!value.trim() || t('Enter your name.'),
    emailRule: value => /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value.trim()) || t('Enter a valid email address.'),
    messageRule: value => value.trim().length >= 10 || t('Please describe your request (at least 10 characters).'),
  }),
  watch: {
    async value(open) {
      if (!open) return
      if (this.receipt) {
        this.form.message = ''
        this.receipt = ''
        this.requestId = ''
        this.lastPayload = ''
      }
      this.form.topic = this.topic
      this.error = ''
      try {
        const user = await Auth.currentAuthenticatedUser()
        if (!this.form.email) this.form.email = user.attributes.email || ''
        if (!this.form.name) this.form.name = user.attributes.name || ''
      } catch (_) { /* Account recovery remains available without signing in. */ }
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation() })
    },
  },
  methods: {
    async submit() {
      if (this.sending || !this.$refs.form.validate()) return
      const payload = JSON.stringify(this.form)
      if (!this.requestId || payload !== this.lastPayload) this.requestId = supportRequestId()
      this.lastPayload = payload
      this.sending = true
      this.error = ''
      try {
        const result = await support.send({ ...this.form }, this.requestId)
        this.receipt = result.id
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message) || t('Unable to send your message. Please try again.')
      } finally { this.sending = false }
    },
  },
}
</script>
<style scoped>
.support-dialog-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 12px; }
.support-trap { position: absolute; left: -10000px; width: 1px; height: 1px; }
</style>
